import { type ActionsObject, makeAction, makeReducer, type ReduxAction } from '@finn-no/redux-actions';
import type { CheckboxMap, Filters } from '@/interfaces';
import { defaultState } from './defaultState';

export const actions: ActionsObject<Filters> = {};

const toggleCheckbox = (checkboxes: CheckboxMap, value: string): CheckboxMap => {
    const { [value]: toggledOn, ...rest } = checkboxes;
    return toggledOn ? rest : { ...rest, [value]: true };
};

actions.setFilters = makeAction(
    'FILTER/SET_FILTERS',
    (state: Filters, action): Filters => ({
        ...state,
        ...action.payload,
    }),
);

actions.changeSort = makeAction(
    'FILTER/CHANGE_SORT',
    (state: Filters, action): Filters => ({
        ...state,
        sort: action.payload,
        page: undefined,
    }),
);

actions.changePage = makeAction(
    'FILTER/CHANGE_PAGE',
    (state: Filters, action): Filters => ({
        ...state,
        page: action.payload,
    }),
);

actions.changeMapBounds = makeAction(
    'FILTER/CHANGE_MAP_BOUNDS',
    (state: Filters, action): Filters => ({
        ...state,
        mapBounds: action.payload,
        page: undefined,
    }),
);

actions.changeDates = makeAction(
    'FILTER/CHANGE_DATES',
    (state: Filters, action): Filters => ({
        ...state,
        checkInDate: action.payload.checkInDate,
        checkOutDate: action.payload.checkOutDate,
    }),
);

actions.changeRooms = makeAction(
    'FILTER/CHANGE_ROOMS',
    (state: Filters, action): Filters => ({
        ...state,
        rooms: action.payload,
    }),
);

actions.filterStarRating = makeAction(
    'FILTER/TOGGLE_STAR_RATING',
    (state: Filters, action): Filters => ({
        ...state,
        starRating: toggleCheckbox(state.starRating, action.payload),
        page: undefined,
    }),
);

actions.filterGuestRating = makeAction(
    'FILTER/TOGGLE_GUEST_RATING',
    (state: Filters, action): Filters => ({
        ...state,
        guestRating: toggleCheckbox(state.guestRating, action.payload),
        page: undefined,
    }),
);

actions.filterHotelChain = makeAction(
    'FILTER/TOGGLE_HOTEL_CHAIN',
    (state: Filters, action): Filters => ({
        ...state,
        hotelChain: toggleCheckbox(state.hotelChain, action.payload),
        page: undefined,
    }),
);

actions.filterFacility = makeAction(
    'FILTER/TOGGLE_FACILITY',
    (state: Filters, action): Filters => ({
        ...state,
        facility: toggleCheckbox(state.facility, action.payload),
        page: undefined,
    }),
);

actions.filterPropertyType = makeAction(
    'FILTER/TOGGLE_PROPERTY_TYPE',
    (state: Filters, action): Filters => ({
        ...state,
        propertyType: toggleCheckbox(state.propertyType, action.payload),
        page: undefined,
    }),
);

actions.filterHotelPageCustom = makeAction(
    'FILTER/TOGGLE_HOTEL_PAGE_CUSTOM',
    (state: Filters, action): Filters => ({
        ...state,
        hotelPageCustom: toggleCheckbox(state.hotelPageCustom, action.payload),
    }),
);

actions.filterFavoriteHotels = makeAction(
    'FILTER/TOGGLE_FAVORITE_HOTELS',
    (state: Filters, action): Filters => ({
        ...state,
        favoriteHotels: toggleCheckbox(state.favoriteHotels, action.payload),
        page: undefined,
    }),
);

actions.clearHotelPageCustom = makeAction(
    'FILTER/CLEAR_HOTEL_PAGE_CUSTOM',
    (state: Filters): Filters => ({
        ...state,
        hotelPageCustom: {},
    }),
);

actions.filterPrice = makeAction(
    'FILTER/SET_PRICE',
    (state: Filters, action): Filters => ({
        ...state,
        price: action.payload,
        page: undefined,
    }),
);

actions.filterByHotelName = makeAction(
    'FILTER/SET_HOTEL_NAME',
    (state: Filters, action): Filters => ({
        ...state,
        hotelName: action.payload,
        page: undefined,
    }),
);

actions.setPreferredHotels = makeAction(
    'FILTER/SET_PREFERRED_HOTELS',
    (state: Filters, action: ReduxAction): Filters => ({
        ...state,
        preferredHotels: action.payload,
    }),
);

actions.resetFilters = makeAction(
    'FILTER/RESET',
    (state: Filters): Filters => ({
        ...defaultState.filters,
        destination: state.destination,
        checkInDate: state.checkInDate,
        checkOutDate: state.checkOutDate,
        rooms: state.rooms,
        hotelIds: state.hotelIds,
    }),
);

export const filterReducer = makeReducer<Filters>(actions);
